/* eslint-disable max-lines */
/* eslint-disable react-func/max-lines-per-function */
import { isString } from 'lodash-es';
import { inRange, isNil, isObject } from 'lodash-es';
import { FileDetail, FileCheckRuleItem, FileCheckResult, CheckItem, SORT_INDEX, ExportVersionFile } from 'common/types';
import type { CollapsePanel, CollapseRow } from 'common/components/CollapseTree';

import {
  CheckMethodMap,
  CheckType,
  FILE_RULES_OF_DEFAULT_SHOW,
  FilterCheckItemMap,
  VersionFileTypeMap,
} from 'common/constants';
import { mapToPreviewType } from './versionDetail';
import { formatCheckResult, formatCheckValue } from './versionDetailFormat';
import { formatSize } from './utils';

export const filterCheckList = (checkList) => {
  if (!Array.isArray(checkList)) {
    return { filteredCheckFiles: [], filteredCheckList: [] };
  }
  // 0.7.1 注释
  // const filteredCheckList = sortCheckFilesByExportFileType(
  //   checkList.filter(({ type, status }) => type !== 'attachment' && status !== FileCheckResult.Ignore),
  // );
  const filteredCheckList = checkList;
  // group、material不是文件
  const filteredCheckFiles = sortCheckFilesByExportFileType(
    filteredCheckList?.filter((item) => {
      if (item && item.type) {
        return ![CheckType.GroupCheck, CheckType.MaterialCheck].includes(item.type as CheckType);
      } else {
        return true;
      }
    }),
  );
  return { filteredCheckFiles, filteredCheckList };
};

const getGroupNameFromArray = (objectArray) => {
  if (!objectArray) {
    return '';
  }
  return `${objectArray[0]['regExp']}`;
};

const getErrorCheckItem = (checkItem: CheckItem): CollapsePanel | null => {
  if (checkItem.status === FileCheckResult.NotPass) {
    //解析checkItem下的rules，哪个rule是NotPass的，解析这个rule的文案
    const errorRules = checkItem.rules?.filter((rule) => rule.status === FileCheckResult.NotPass);
    errorRules?.sort((a, b) => a.id! - b.id!);
    let namedErrorRules: CollapseRow[] = [];
    errorRules?.forEach((rule) => {
      const formatResult = formatCheckResult(rule.ruleType, rule?.checkResult); //质检结果值
      const formatValue = formatCheckValue(rule.ruleType, rule?.checkValue); //质检目标值

      if (formatResult || formatValue) {
        namedErrorRules.push({
          label: rule.ruleLabel,
          value: formatResult,
          submitMessage: rule.submitMessage
            ?.replace(/\${checkValue}/, formatValue as any)
            .replace(/\${checkMethod}/, CheckMethodMap[rule.checkMethod]),
        });
      }
    });
    let header = '';
    if (checkItem.type === 'Group') {
      header = `${Array.isArray(checkItem.name) ? getGroupNameFromArray(checkItem.name) : checkItem.name}(Group)`;
      // group reason === EMPTY， 不展示rules结果
      const groupNotFind = checkItem.reason === 'EMPTY';
      groupNotFind &&
        (namedErrorRules = [
          {
            label: checkItem.fileName_desc || checkItem.fileNameDesc || checkItem.name || `group`,
            value: '未找到',
            submitMessage: '',
          },
        ]);
    } else if (checkItem.type === 'Material') {
      header = `checkItem.name (Material)`;
    } else {
      header = checkItem.fileName;
    }
    return {
      type: checkItem.type || '',
      list: namedErrorRules,
      header: header,
      key: checkItem.name || checkItem.fileName,
      children: checkItem.children as any,
      panelChildren: [],
      itemId: checkItem.itemId,
    };
  } else {
    return null;
  }
};

/**
 * 把需求模版的checkList转换【提交规范】折叠面板CollapseTree显示内容
 * 规则：
 * 1.组装规则：见parseCheckListToPanelRow
 * 2. 规则内容：显示submitMessage，替换submitMessage中的checkValue
 * 3. header规则：VersionFileTypeMap
 * @param checkList 需求模版的checkList
 * @returns CollapsePanelType[]
 */
export function parseCheckListToCommitPanelRow(checkList: CheckItem[]) {
  return parseCheckListToPanelRow(
    checkList,
    (rule) => {
      const formattedValue = formatCheckValue(rule.ruleType, rule?.checkValue, '‘未找到约束参数’');
      return rule?.submitMessage
        ? (rule.submitMessage
            .replace(/\${checkValue}/, formattedValue as any)
            .replace(/\${checkMethod}/, CheckMethodMap[rule.checkMethod]) as any)
        : formattedValue;
    },
    (item) => VersionFileTypeMap[item.type] || item.type,
    'commit',
  );
}

/**
 * 把需求模版的checkList转换【文件信息】折叠面板CollapseTree显示内容
 * 规则：
 * 1.组装规则：见parseCheckListToPanelRow
 * 2. 规则内容：显示checkResult
 * 3. header规则：文件名
 * @param checkList 需求模版的checkList
 * @returns CollapsePanelType[]
 */
export function parseCheckListToDetailPanelRow(checkList: CheckItem[]) {
  return parseCheckListToPanelRow(
    checkList,
    (rule) => {
      const formatCheckValue = formatCheckResult(rule.ruleType, rule?.checkResult);
      return formatCheckValue;
    },
    (item) => item.name || item.fileName,
    'fileDetail',
  );
}

/**
 * 把需求模版的checkList转换折叠面板CollapseTree显示内容
 * 规则：0.2.1版本不显示type为material类型，type为Group内容需要嵌套在type为dccFile下
 *  1. 把所有的type为group 放到type为dccfile下
 * @param checkList 需求模版的checkList
 * @param handleRuleValue rule 内容显示规则
 * @param handleHeader 面板header显示规则
 * @returns CollapsePanelType[]
 */
export function parseCheckListToPanelRow(
  checkList: CheckItem[],
  handleRuleValue: (rule: FileCheckRuleItem) => string | string[],
  handleHeader: (checkItem: CheckItem) => string,
  type: 'commit' | 'fileDetail',
): CollapsePanel[] {
  let rows: CollapsePanel[] = [];
  const groups: CollapsePanel[] = [];

  checkList.forEach((item: CheckItem, index) => {
    let formattedRule: CollapseRow[] = [];
    if (type === 'commit') {
      const isFile = !item?.name;
      isFile &&
        formattedRule.unshift({
          label: '文件格式',
          value: Array.isArray(item.fileType) ? item.fileType.join(',') : item.fileType || '*',
        });
      formattedRule.unshift({
        label: '文件名',
        value: isString(item.fileName_desc || item.fileNameDesc || item.fileName)
          ? item.fileName_desc || item.fileNameDesc || item.fileName
          : '',
      });
    }

    if (item.type !== 'Material') {
      let rules = [...(item.rules ?? [])];
      const newRules = [...rules];
      newRules.sort((a, b) => {
        const aValue = FilterCheckItemMap[a.ruleType] || 17;
        const bValue = FilterCheckItemMap[b.ruleType] || 17;
        return aValue - bValue;
      });

      rules?.forEach((rule: FileCheckRuleItem) => {
        if (type === 'fileDetail') {
          // const isShow = FILE_RULES_OF_DEFAULT_SHOW.includes(rule.ruleType as any);
          const isShow = !item.isShowRule;
          const value = handleRuleValue(rule);
          isShow &&
            value &&
            formattedRule.push({
              label: rule.ruleLabel,
              value,
            });
        } else {
          if (rule.isShowRule || rule.outputValue) {
            const value = handleRuleValue(rule);
            value &&
              formattedRule.push({
                label: rule.ruleLabel,
                value,
              });
          }
        }
      });

      if (item.type === 'Group') {
        // group reason === EMPTY， 没找到group，不展示group信息
        const groupNotFind = item?.reason === 'EMPTY';
        !groupNotFind &&
          groups.push({
            header: `Group（组）: ${handleHeader(item)}`,
            key: `${item.fileName_desc || item.fileNameDesc}`,
            type: item.type,
            list: formattedRule,
            panelChildren: [],
            children: item.children as any,
            itemId: item.itemId,
          });
      } else if (item.type === 'DCCFile') {
        rows.unshift({
          header: handleHeader(item),
          key: `${
            isString(item.fileName_desc || item.fileNameDesc || item.fileName)
              ? item.fileName_desc || item.fileNameDesc || item.fileName
              : ''
          }`,
          type: item.type,
          list: formattedRule,
          itemId: item.itemId,
          panelChildren: [],
          children: item.children as any,
        });
      } else {
        rows.push({
          header: handleHeader(item),
          key: `${
            isString(item.fileName_desc || item.fileNameDesc || item.fileName)
              ? item.fileName_desc || item.fileNameDesc || item.fileName
              : ''
          }`,
          type: item.type || '',
          list: formattedRule,
          itemId: item.itemId,
          children: item.children as any,
          panelChildren: [],
        });
      }
    }
  });

  rows = insertGroupToDCCFile(rows, groups);

  // console.log('parseCheckListToPanelRow', { checkList, rows });
  return rows;
}

function insertGroupToDCCFile(rows: CollapsePanel[], groups: CollapsePanel[]) {
  // 兼容023数据
  let dccIndex = -1,
    isVersion030 = true;
  rows.forEach((row, index) => {
    if (row.type === 'DCCFile') {
      dccIndex = index;
    }
  });
  if (dccIndex > -1) {
    const dccFile = rows[dccIndex];
    if (dccFile.children) {
      isVersion030 = true;
    }
  }
  if (isVersion030) {
    rows.forEach((row, index) => {
      if (row.type === 'DCCFile') {
        //去寻找DCCFile的children
        const children = row.children?.slice(0);
        const newChildren: CollapsePanel[] = [];
        if (children && children.length > 0) {
          children.forEach((child) => {
            const groupIndex = groups.findIndex((group) => child === group.itemId);
            if (groupIndex > -1) {
              newChildren.push(groups[groupIndex]);
            }
          });
        }
        row.panelChildren = newChildren;
      }
    });
  } else {
    // 兼容旧数据
    if (dccIndex > -1) {
      rows[dccIndex].panelChildren = groups;
    }
  }
  return rows;
}
/**
 * 解析文件信息，处理服务端返回的附件信息
 * @param fileDetail 附件列表
 * @returns CollapsePanelType[]
 */
export function parseFileDetailToPanelRow(fileDetail: FileDetail[]): CollapsePanel[] {
  const rows: CollapsePanel[] = [];
  fileDetail.forEach((item, index) => {
    let cur: CollapsePanel = {
      header: item.name || `附件-${index}`,
      type: item.type || '',
      list: [] as CollapseRow[],
      key: `${item.name}-${index}`,
      panelChildren: [] as CollapsePanel[],
    };
    if (item.detail && item.detail.length > 0) {
      const formattedRule: CollapseRow[] = [];
      item.detail.forEach((rule: FileCheckRuleItem) => {
        if (FILE_RULES_OF_DEFAULT_SHOW.includes(rule.ruleType as any)) {
          const result = formatCheckResult(rule.ruleType, rule?.checkResult);

          if (result) {
            formattedRule.push({
              label: rule.ruleLabel,
              value: result,
            });
          }
        }
      });
      cur.list = formattedRule;
    }
    rows.push(cur);
  });
  // console.log('parseFileDetailToPanelRow', { fileDetail, rows });
  return rows;
}
/**
 * 解析组规则的checkResult
 * @param checkList
 * @returns
 */
export function parseGroupDetails(checkList: CheckItem[]): CollapsePanel[] {
  const groups: CollapsePanel[] = [];
  checkList.forEach((item, index) => {
    let cur: CollapsePanel = {
      header: `${item.name || ''}(Group)`,
      type: item.type || '',
      list: [] as CollapseRow[],
      key: `${item.name}`,
      panelChildren: [] as CollapsePanel[],
    };

    if (item.rules && item.rules.length > 0) {
      const formattedRule: CollapseRow[] = [];
      item.rules.forEach((rule: FileCheckRuleItem) => {
        if (FILE_RULES_OF_DEFAULT_SHOW.includes(rule.ruleType as any)) {
          const result = formatCheckResult(rule.ruleType, rule?.checkResult);

          if (result) {
            formattedRule.push({
              label: rule.ruleLabel,
              value: result,
            });
          }
        }
      });
      cur.list = formattedRule;
    }

    groups.push(cur);
  });
  // console.log('parseGroupDetails', { checkList, groups });
  return groups;
}
/**
 * 解析版本详情，展示错误的checkValue
 * @param checkList 版本详情
 * @returns
 */
export function parseCheckErrorToPanelRow(checkList: CheckItem[]): CollapsePanel[] {
  // console.log('parsCheckErrorToPanelRow origin checkList', checkList.slice(0));
  const groups: CollapsePanel[] = [];
  let rows: CollapsePanel[] = [];
  checkList.forEach((item) => {
    if (item.type === 'Group') {
      const filteredErrorItem = getErrorCheckItem(item);
      if (filteredErrorItem) {
        groups.push(filteredErrorItem);
      }
    } else if (item.type !== 'Material') {
      const filteredErrorItem = getErrorCheckItem(item);
      if (filteredErrorItem) {
        rows.push(filteredErrorItem);
      }
    }
  });
  rows = insertGroupToDCCFile(rows, groups);
  //遍历rows,如果有label为总帧数或者帧率的数据，需要把对象格式化为字符串
  rows.forEach((row) => {
    row.list.map((rule) => {
      if (rule.label === '总帧数' || rule.label === '帧率') {
        if (Array.isArray(rule.value)) {
          let str: string[] = [];
          rule.value.forEach((rule) => {
            let s: string[] = [];
            const keys = Object.keys(rule);

            keys.forEach((k) => {
              s.push(`${rule?.[k] || ''}`);
            });
            str.push(s.join(':'));
          });
          rule.value = str.join(',');
        }
      }
    });
  });
  // console.log('parseCheckErrorToPanelRow rows!!!', rows);
  return rows;
}

export function sortCheckFilesByExportFileType(checkFiles: ExportVersionFile[]) {
  const group = groupFilesByExAndSortByName(checkFiles);
  const filteredList: ExportVersionFile[] = [];
  Object.keys(group)
    .sort((a, b) => {
      return SORT_INDEX[a] - SORT_INDEX[b];
    })
    .forEach((key) => {
      filteredList.push(...group[key]);
    });
  return filteredList;
}
// 同文件后缀进行排序
export function groupFilesByExAndSortByName(checkFiles: ExportVersionFile[]) {
  const group = {};
  checkFiles.forEach((item) => {
    const type = mapToPreviewType(item);
    if (!group[type]) {
      group[type] = [];
    }
    group[type].push(item);
  });
  Object.keys(group).forEach((key) => {
    group[key].sort((a, b) => {
      return a.fileName.localeCompare(b.fileName);
    });
  });
  return group;
}

export const FILE_SUFFIX_TO_EXPORT_FILE_TYPE = {
  babylon: ['babylon', 'gltf', 'glb'],
};

export function getExportFileTypeFromFileSuffix(taskType: string, fileName: string) {
  const fileSuffix = fileName.split('.').pop();
  if (fileSuffix) {
    return FILE_SUFFIX_TO_EXPORT_FILE_TYPE[fileSuffix];
  }
}
